import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, AlertCircle, ChevronLeft, ChevronRight, History, ArrowLeft } from 'lucide-react';
import { planApi, SubscriptionHistoryItem, SubscriptionStatus } from '@/api/planApi';

// ── 상수 ─────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const STATUS_META: Record<SubscriptionStatus, { label: string; className: string }> = {
  FREE:               { label: '무료',             className: 'bg-gray-100 text-gray-600 border-gray-300' },
  PENDING_UPGRADE:    { label: '입금 대기',         className: 'bg-blue-100 text-blue-700 border-blue-300' },
  ACTIVE_PAID:        { label: '유료 이용 중',      className: 'bg-green-100 text-green-700 border-green-300' },
  PENDING_DOWNGRADE:  { label: '다운그레이드 예약', className: 'bg-amber-100 text-amber-700 border-amber-300' },
};

const CHANGED_BY_TYPE_LABEL: Record<string, string> = {
  USER:   '사용자',
  ADMIN:  '관리자',
  SYSTEM: '시스템',
};

const REASON_LABEL: Record<string, string> = {
  UPGRADE_REQUEST:          '업그레이드 요청',
  PAYMENT_CONFIRMED:        '입금 확인',
  PAYMENT_TIMEOUT:          '입금 대기 만료',
  DOWNGRADE_REQUEST:        '다운그레이드 요청',
  DOWNGRADE_APPLIED:        '다운그레이드 적용',
  DOWNGRADE_CANCELLED:      '다운그레이드 취소',
  ADMIN_OVERRIDE:           '관리자 강제 보정',
};

function formatChangeReason(reason: string): string {
  return REASON_LABEL[reason] ?? reason;
}

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function SubscriptionHistoryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<SubscriptionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const load = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await planApi.getSubscriptionHistory({ page, size: PAGE_SIZE });
      setItems(res.data.content ?? []);
      setTotalPages(res.data.totalPages ?? 0);
      setTotalElements(res.data.totalElements ?? 0);
      setCurrentPage(res.data.number ?? page);
    } catch {
      setError('구독 상태 변경 이력을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  const formatDateTime = (iso: string) =>
    iso
      ? new Date(iso).toLocaleString('ko-KR', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        })
      : '-';

  return (
    <div className="space-y-6 p-4 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800"
          onClick={() => navigate('/dashboard/my/plan')}
        >
          <ArrowLeft className="w-4 h-4" />
          요금제 관리
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <History className="w-5 h-5 text-gray-600" />
        <div>
          <h1 className="text-xl font-bold">구독 상태 변경 이력</h1>
          <p className="text-sm text-gray-500 mt-0.5">플랜 및 구독 상태가 변경된 내역을 확인합니다.</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            전체 {totalElements > 0 ? `${totalElements.toLocaleString()}건` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-gray-400 py-16 text-sm">구독 상태 변경 이력이 없습니다.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">변경 일시</TableHead>
                    <TableHead className="text-xs">변경 전</TableHead>
                    <TableHead className="text-xs">변경 후</TableHead>
                    <TableHead className="text-xs">사유</TableHead>
                    <TableHead className="text-xs">처리 주체</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const fromMeta = STATUS_META[item.fromStatus] ?? { label: item.fromStatus, className: 'bg-gray-100 text-gray-600' };
                    const toMeta   = STATUS_META[item.toStatus]   ?? { label: item.toStatus,   className: 'bg-gray-100 text-gray-600' };
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                          {formatDateTime(item.changedAt)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${fromMeta.className}`}>
                            {fromMeta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${toMeta.className}`}>
                            {toMeta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-700">
                          {formatChangeReason(item.reason)}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {CHANGED_BY_TYPE_LABEL[item.changedByType] ?? item.changedByType}
                          {item.changedById && item.changedByType === 'ADMIN' && (
                            <span className="text-gray-400 ml-1">({item.changedById})</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage === 0 || loading}
            onClick={() => load(currentPage - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">
            {currentPage + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages - 1 || loading}
            onClick={() => load(currentPage + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
